import { useMemo, useState } from 'react';
import { pcompany, ptax } from '@/proto/proto';
import { Badge, Tag, Tooltip } from 'antd';
import { Button, Col, Row, Space, Text } from '@/components/atoms';
import { FORMAT_DATE_GENERAL_EXCEPT_SECOND, IMAGE_PATH_TAX_CONTRACT, IMAGE_PATH_TAX_NOTICE, PATH_TAX_NOTICE_DETAIL } from '@/constants/constant';
import TaxNoticeStatusText from '@/components/molecules/tax/notice/TaxNoticeStatusText';
import moment from 'moment';
import Highlighter from 'react-highlight-words';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import TaxNoticeStatus from '@/components/covalent/tax/notice/TaxNoticeStatus';
import TaxNoticeChangeTypeText from '@/components/molecules/tax/notice/TaxNoticeChangeTypeText';
import TaxNoticeSearchContractStatus from '@/components/covalent/tax/notice/TaxNoticeSearchContractStatus';
import TaxNoticeMatchOrgStatus from '@/components/covalent/tax/notice/TaxNoticeMatchOrgStatus';
import { Link } from 'react-router-dom';

type TaxNoticeListItemProps = {
  data: ptax.IPbTaxNotice;
  company: pcompany.IPbCompany;
  loading?: boolean;
  empty?: string;
  keyword: string;
  onClickImage?: (data: ptax.IPbTaxNotice) => void;
  onClickDetail?: (data: ptax.IPbTaxNotice) => void;
  onClickSend?: (data: ptax.IPbTaxNotice) => void;
  onClickUploadContract?: (data: ptax.IPbTaxNotice) => void;
  onClickRegisterContact?: (data: ptax.IPbTaxContact) => void;
  onClickChangeInformation?: (data: ptax.IPbTaxNotice) => void;
};

const TaxNoticeListItem = ({
  data,
  company,
  loading,
  empty,
  keyword,
  onClickImage,
  onClickDetail,
  onClickSend,
  onClickUploadContract,
  onClickRegisterContact,
  onClickChangeInformation,
}: TaxNoticeListItemProps) => {
  const violationLocation = useMemo(() => {
    const value = data.violation?.first()?.location;
    if (value) {
      return value.length > 20 ? `${value.substring(0, 20)}...` : value;
    }
    return undefined;
  }, [data.violation]);

  const details = useMemo(() => {
    const value = data.details;
    if (value) {
      return value;
    }
    return data.violation?.first()?.details;
  }, [data.details, data.violation]);

  const violationAt = useMemo(() => {
    const value = data.violation?.first()?.violationAt;
    if (value) {
      return moment(value).format(FORMAT_DATE_GENERAL_EXCEPT_SECOND);
    }
    return undefined;
  }, [data.violation]);

  const information = useMemo(() => {
    switch (data.status) {
      case TaxNoticeStatus.STAY:
        switch (data.matchOrgStatus) {
          case TaxNoticeMatchOrgStatus.SYSTEM:
          case TaxNoticeMatchOrgStatus.USER:
            return Number(data.searchContactStatus) > TaxNoticeSearchContractStatus.READY ? (
              <Space direction="vertical" align="center">
                <TaxNoticeStatusText data={data} />
                <Button type="success" size="small" shape="round" onClick={() => onClickSend && onClickSend(data)}>
                  민원 등록
                </Button>
              </Space>
            ) : (
              <Space direction="vertical" align="center">
                <TaxNoticeStatusText data={data} />
              </Space>
            );
          default:
            return (
              <Space direction="vertical" align="center">
                <TaxNoticeStatusText data={data} />
              </Space>
            );
        }
      case TaxNoticeStatus.DONE:
        return (
          <Space direction="vertical" align="center">
            <TaxNoticeStatusText data={data} />
            <TaxNoticeChangeTypeText data={data} />
          </Space>
        );
      default:
        return undefined;
    }
  }, [data, onClickSend]);

  const functions = useMemo(() => {
    switch (data.status) {
      case TaxNoticeStatus.STAY:
        switch (data.matchOrgStatus) {
          case TaxNoticeMatchOrgStatus.USER:
          case TaxNoticeMatchOrgStatus.SYSTEM:
            switch (data.searchContactStatus) {
              case TaxNoticeSearchContractStatus.USER:
              case TaxNoticeSearchContractStatus.SYSTEM:
                return (
                  <Space direction="vertical">
                    <Button type="default" shape="round" onClick={() => onClickDetail && onClickDetail(data)}>
                      고지서 확인
                    </Button>
                    <Button type="default" shape="round">
                      <a href={IMAGE_PATH_TAX_CONTRACT(Number(company.base?.id), data)}>계약서 확인</a>
                    </Button>
                  </Space>
                );
              default:
                return (
                  <Space direction="vertical">
                    <Button type="default" shape="round" onClick={() => onClickDetail && onClickDetail(data)}>
                      고지서 확인
                    </Button>
                    <Button type="lightdark" shape="round" onClick={() => onClickUploadContract && onClickUploadContract(data)}>
                      계약서 등록
                    </Button>
                  </Space>
                );
            }
          default:
            return (
              <Space direction="vertical">
                <Button type="lightdark" shape="round" onClick={() => onClickDetail && onClickDetail(data)}>
                  고지서 수정
                </Button>
              </Space>
            );
        }
      case TaxNoticeStatus.DONE:
        return (
          <Space direction="vertical">
            <Button type="default" shape="round" onClick={() => onClickChangeInformation && onClickChangeInformation(data)}>
              민원 정보
            </Button>
          </Space>
        );
      default:
        return undefined;
    }
  }, [company.base, data, onClickChangeInformation, onClickDetail, onClickUploadContract]);

  return (
    <Row className="list-item">
      <Col xl={2} lg={3} md={24} sm={24} xs={24} className="date" style={{ padding: '4px' }}>
        <Space direction="vertical" align="center">
          <Tag style={{ borderRadius: '20px', minWidth: '4em', textAlign: 'center', margin: 0 }} color="#999999">
            {data?.base?.id}
          </Tag>
          <Text style={{ textOverflow: 'ellipsis' }}>{moment(data.base?.createdAt).fromNow()}</Text>
        </Space>
      </Col>
      <Col xl={2} lg={3} md={24} sm={24} xs={24} className="id" style={{ padding: '4px' }}>
        <LazyLoadImage src={IMAGE_PATH_TAX_NOTICE(Number(company.base?.id), data)} onClick={() => onClickImage && onClickImage(data)} />
      </Col>
      <Col style={{ flex: 1, padding: '8px' }}>
        <Row style={{ width: '100%' }}>
          <Col span={24}>
            <Tag>발급기관</Tag>
            <Highlighter searchWords={keyword.split(' ')} textToHighlight={data?.sender ?? empty ?? ''} />
            {!data.contact && Number(data.matchOrgStatus) < 0 && (
              <Tooltip
                style={{ margin: '8px' }}
                trigger="click"
                placement="top"
                title={
                  <Space direction="horizontal">
                    기관 정보가 없습니다.
                    <Button type="lightdark" size="small" onClick={() => onClickRegisterContact && onClickRegisterContact(data)}>
                      등록 요청
                    </Button>
                  </Space>
                }
              >
                <Badge count="!" key="default" />
              </Tooltip>
            )}
          </Col>
          <Col span={24}>
            <Tag>차량번호</Tag>
            <Highlighter searchWords={keyword.split(' ')} textToHighlight={data.plateNo ?? empty ?? ''} />
          </Col>
          <Col span={24}>
            <Tag>위반정보</Tag>
            <Text ellipsis>
              {violationAt}
              {violationLocation ? ' / ' : ''}
              {violationLocation}
              {!violationAt && data.recognizeStatus !== 0 && (
                <Tooltip
                  style={{ margin: '8px' }}
                  trigger="click"
                  placement="top"
                  title={
                    <Space direction="horizontal">
                      정보를 입력해주세요.
                      <Button type="lightdark" size="small">
                        <Link to={PATH_TAX_NOTICE_DETAIL(data)}>직접입력</Link>
                      </Button>
                    </Space>
                  }
                >
                  <Badge key="orange" count="!" style={{ backgroundColor: 'orange' }} />
                </Tooltip>
              )}
            </Text>
          </Col>
        </Row>
      </Col>
      <Col xl={3} lg={3} md={3} sm={24} xs={24} className="detail">
        {information}
      </Col>
      <Col xl={3} lg={3} md={3} sm={24} xs={24} className="function">
        {functions}
      </Col>
    </Row>
  );
};

TaxNoticeListItem.defaultProps = {
  loading: true,
  empty: '-',
  onClickImage: undefined,
  onClickDetail: undefined,
  onClickSend: undefined,
  onClickUploadContract: undefined,
  onClickRegisterContact: undefined,
  onClickChangeInformation: undefined,
};

export default TaxNoticeListItem;
